import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { BookOpen, Search, Printer, Copy, Check, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SOURCE_COLORS = {
  cliente: 'bg-blue-50 text-blue-700 border-blue-200',
  empresa: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  sistema: 'bg-green-50 text-green-700 border-green-200',
  documento: 'bg-amber-50 text-amber-700 border-amber-200',
  itens: 'bg-purple-50 text-purple-700 border-purple-200',
  var_documento: 'bg-red-50 text-red-700 border-red-200',
  premissa: 'bg-orange-50 text-orange-700 border-orange-200',
};

const VariableDictionaryPage = () => {
  const [data, setData] = useState({ variables: [], table_loop: null, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [copiedVar, setCopiedVar] = useState('');
  const printRef = useRef();

  const loadDictionary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/variable-dictionary`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error('Failed to load dictionary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDictionary(); }, [loadDictionary]);

  const copyTag = (tag) => {
    navigator.clipboard.writeText(tag);
    setCopiedVar(tag);
    toast.success(`Copiado: ${tag}`);
    setTimeout(() => setCopiedVar(''), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const filtered = data.variables.filter(v => {
    const matchSearch = !search || v.name.toLowerCase().includes(search.toLowerCase()) || v.label.toLowerCase().includes(search.toLowerCase());
    const matchSource = !filterSource || v.source === filterSource || v.category === filterSource;
    return matchSearch && matchSource;
  });

  // Group by source
  const grouped = {};
  filtered.forEach(v => {
    const key = v.source_label || v.source;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(v);
  });

  const sources = [...new Set(data.variables.map(v => v.source))];

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6 print:space-y-4" data-testid="variable-dictionary-page" ref={printRef}>
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Dicionario de Variaveis</h1>
          <p className="text-[#6E6E73]">{data.total} variaveis disponiveis para uso em templates</p>
        </div>
        <Button onClick={handlePrint} variant="outline" data-testid="print-dictionary-btn">
          <Printer className="w-4 h-4 mr-2" />Imprimir / Exportar
        </Button>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-2xl font-bold">ASI Docs - Dicionario de Variaveis</h1>
        <p className="text-sm text-gray-500">{data.total} variaveis - Gerado em {new Date().toLocaleDateString('pt-BR')}</p>
      </div>

      <div className="flex items-center gap-3 print:hidden">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#AEAEB2]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar variavel..." className="pl-10" data-testid="dict-search" />
        </div>
        <Select value={filterSource || 'all'} onValueChange={(val) => setFilterSource(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Todas as fontes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as fontes</SelectItem>
            {sources.map(s => <SelectItem key={s} value={s}>{data.variables.find(v => v.source === s)?.source_label || s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tip: Table Loop */}
      {data.table_loop && !filterSource && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 print:border print:p-3" data-testid="table-loop-info">
          <h3 className="text-sm font-bold text-purple-800 mb-2">Tabela de Itens (Loop no Template DOCX)</h3>
          <p className="text-xs text-purple-700 mb-2">Para criar uma tabela de itens no template, use a sintaxe de loop do docxtpl:</p>
          
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-purple-600 uppercase mb-1">Todos os itens</p>
              <code className="text-xs bg-white px-3 py-2 rounded border border-purple-200 block font-mono text-purple-900">
                {data.table_loop.loop_syntax}
              </code>
            </div>

            {data.table_loop.category_loops && (
              <div>
                <p className="text-[10px] font-semibold text-purple-600 uppercase mb-1">Por categoria (tabelas separadas)</p>
                <code className="text-xs bg-white px-3 py-2 rounded border border-purple-200 block font-mono text-purple-900">
                  {data.table_loop.category_loops.syntax_example}
                </code>
                <p className="text-[10px] text-purple-600 mt-1">{data.table_loop.category_loops.description}</p>
                <p className="text-[10px] text-purple-600">Totais por categoria: {data.table_loop.category_loops.totals}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            {data.table_loop.fields.map(f => (
              <button key={f.name} onClick={() => copyTag(`{{${f.name}}}`)} className="text-left text-xs bg-white rounded px-2 py-1.5 border border-purple-200 hover:border-purple-400 transition-colors">
                <code className="font-mono font-bold text-purple-700">{`{{${f.name}}}`}</code>
                <p className="text-purple-600 mt-0.5">{f.label}</p>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-purple-600 mt-2">Itens de categorias distintas podem ser exibidos na mesma tabela ou em tabelas separadas por categoria</p>
        </div>
      )}

      {/* Variables grouped by source */}
      {Object.entries(grouped).map(([sourceName, vars]) => (
        <div key={sourceName} className="bg-white border border-[#E5E5EA] rounded-xl overflow-hidden print:break-inside-avoid">
          <div className="bg-[#F7F7F8] px-4 py-3 border-b border-[#E5E5EA]">
            <h3 className="text-sm font-bold text-[#111111]">{sourceName}</h3>
            <span className="text-[10px] text-[#AEAEB2]">{vars.length} variavel(is)</span>
          </div>
          <div className="divide-y divide-[#F0F0F0]">
            {vars.map(v => (
              <div key={v.name} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#FAFAFA] group print:py-1.5" data-testid={`dict-var-${v.name}`}>
                <button
                  onClick={() => copyTag(v.template_tag)}
                  className="font-mono text-sm font-bold text-[#AF2628] bg-[#FFF1F0] px-2 py-1 rounded hover:bg-[#AF2628] hover:text-white transition-colors flex items-center gap-1 min-w-[180px] print:bg-transparent print:text-[#AF2628]"
                  title="Clique para copiar"
                >
                  {copiedVar === v.template_tag ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 print:hidden" />}
                  {v.template_tag}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#111111]">{v.label}</p>
                  {v.doc_types && <p className="text-[10px] text-[#6E6E73]">Tipos: {v.doc_types}</p>}
                </div>
                <div className="flex items-center gap-2 print:hidden">
                  {v.auto && <span className="text-[9px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded border border-green-200">Auto</span>}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${SOURCE_COLORS[v.source] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                    {v.source_label || v.source}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="text-center py-12 bg-white border border-[#E5E5EA] rounded-xl">
          <BookOpen className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
          <p className="text-[#6E6E73]">Nenhuma variavel encontrada</p>
        </div>
      )}
    </div>
  );
};

export default VariableDictionaryPage;
